import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box } from "@mui/material";
import ListColumn from "../../../components/Globo/ListColumn/ListColumn";
import Pagination from "../../../components/Globo/Pagination/Pagination";
import { useDebounce } from "../../../hooks/useDebounce";
import {
  getLocalUsers,
  getUserProfiles,
} from "../../../services/api/maintenance/permissionCatalog.api";

const DEFAULT_ROWS_PER_PAGE = 20;

const columnBorderSx = { borderRight: "1px solid #E2E2E3" };

// Visão "Usuário → Perfil → Recurso": audita o que um usuário pode fazer e por quê.
// Somente leitura - perfis/recursos vêm do catálogo já carregado por usePermissionCatalog.
const UserProfileResourceView = ({ profilesByName }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [usersResponse, setUsersResponse] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedProfileName, setSelectedProfileName] = useState(null);
  const [userProfileNames, setUserProfileNames] = useState([]);
  const [loadingUserProfiles, setLoadingUserProfiles] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    setLoadingUsers(true);

    getLocalUsers({
      searchTerm: debouncedSearchTerm,
      pageIndex: page - 1,
      pageSize: rowsPerPage,
      cancelToken: source.token,
    })
      .then((response) => setUsersResponse(response ?? null))
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.error("Erro ao buscar usuários:", error);
        setUsersResponse(null);
      })
      .finally(() => setLoadingUsers(false));

    return () => source.cancel();
  }, [debouncedSearchTerm, page, rowsPerPage]);

  useEffect(() => {
    if (!selectedUserId) {
      setUserProfileNames([]);
      return undefined;
    }

    const source = axios.CancelToken.source();
    setLoadingUserProfiles(true);

    getUserProfiles(selectedUserId, { cancelToken: source.token })
      .then((names) => setUserProfileNames(names ?? []))
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.error("Erro ao buscar perfis do usuário:", error);
        setUserProfileNames([]);
      })
      .finally(() => setLoadingUserProfiles(false));

    return () => source.cancel();
  }, [selectedUserId]);

  const handleSelectUser = (userId) => {
    setSelectedUserId(userId);
    setSelectedProfileName(null);
  };

  const totalItems = usersResponse?.totalCount ?? 0;
  const totalPages = usersResponse?.totalPages || 1;
  const safePage = Math.min(page, totalPages);

  const userItems = (usersResponse?.items ?? []).map((user) => ({
    id: String(user.id),
    label: user.name,
    description: user.email,
  }));

  const profileItems = userProfileNames.map((name) => {
    const profile = profilesByName.get(name);
    return {
      id: name,
      label: name,
      active: profile ? profile.isActive : undefined,
    };
  });

  const selectedProfile = selectedProfileName
    ? profilesByName.get(selectedProfileName)
    : null;

  const resourceItems = (selectedProfile?.resources ?? []).map((name) => ({
    id: name,
    label: name,
  }));

  return (
    <Box
      sx={{
        display: "flex",
        border: "1px solid #E2E2E3",
        borderRadius: "8px",
        height: "100%",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          ...columnBorderSx,
        }}
      >
        <ListColumn
          title="Usuários"
          items={userItems}
          selectedId={selectedUserId}
          onSelect={handleSelectUser}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Buscar por nome ou e-mail"
          loading={loadingUsers}
          emptyMessage="Nenhum usuário encontrado."
          sortAlphabetically={false}
          sx={{ flex: 1 }}
        />
        {totalItems > 0 && (
          <Pagination
            compact
            page={safePage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsLabel="usuários"
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
            onPageChange={setPage}
            onRowsPerPageChange={(nextRowsPerPage) => {
              setRowsPerPage(nextRowsPerPage);
              setPage(1);
            }}
          />
        )}
      </Box>

      <ListColumn
        title="Perfis"
        items={profileItems}
        selectedId={selectedProfileName}
        onSelect={setSelectedProfileName}
        searchable={false}
        loading={loadingUserProfiles}
        emptyMessage={
          selectedUserId
            ? "Este usuário não possui perfis associados."
            : "Selecione um usuário."
        }
        sx={{ flex: 1, ...columnBorderSx }}
      />

      <ListColumn
        title="Recursos"
        items={resourceItems}
        selectedId={null}
        searchable={false}
        emptyMessage={
          selectedProfileName
            ? "Este perfil não concede nenhum recurso."
            : "Selecione um perfil."
        }
        sx={{ flex: 1 }}
      />
    </Box>
  );
};

export default UserProfileResourceView;
